import { ChartBarIcon } from "@heroicons/react/24/outline";
import { Layout } from "../UI/Layout/Layout.tsx";
import { IconButton } from "../UI/IconButton.tsx";
import { Card } from "../UI/Card.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { SegmentedControl } from "../UI/SegmentedControl.tsx";
import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { useTableUrlState } from "../UI/Table/useTableUrlState.ts";
import { OnboardingLiveListPeriod } from "../gql/graphql.ts";
import { useOnboardingLives } from "./useOnboardingLives.ts";
import { useOnboardingLiveCreationIntents } from "./useOnboardingLiveCreationIntents.ts";
import {
  onboardingLiveDetailPathOf,
  onboardingLivePlanPathOf,
  OnboardingLivesTab,
  OnboardingLiveStatisticsScreenPath,
} from "./OnboardingLivePaths.ts";
import { useOnboardingLivesScreenUrlState } from "./List/useOnboardingLivesScreenUrlState.ts";
import { useOpenFromOnboardingLivesList } from "./List/onboardingLivesListNavigation.ts";
import { NewOnboardingLivePlanButton } from "./List/NewOnboardingLivePlanButton.tsx";
import { OnboardingLivesTable } from "./List/OnboardingLivesTable.tsx";
import { OnboardingLivePlansTable } from "./List/OnboardingLivePlansTable.tsx";
import {
  defaultOnboardingLivesSortOf,
  ONBOARDING_LIVES_SORT_FIELDS,
  parseOnboardingLivesFilters,
  writeOnboardingLivesFilters,
} from "./List/onboardingLivesTableState.ts";
import {
  ONBOARDING_LIVE_PLANS_DEFAULT_SORT,
  ONBOARDING_LIVE_PLANS_SORT_FIELDS,
  parseOnboardingLivePlansFilters,
  writeOnboardingLivePlansFilters,
} from "./List/onboardingLivePlansTableState.ts";

function OnboardingLivesList({
  period,
  onPeriodChange,
}: {
  period: OnboardingLiveListPeriod;
  onPeriodChange: (period: OnboardingLiveListPeriod) => void;
}) {
  const openFromList = useOpenFromOnboardingLivesList();
  const { onboardingLives, loading } = useOnboardingLives({ period });
  const { sort, filters, setSort, setFilters } = useTableUrlState({
    sortFields: ONBOARDING_LIVES_SORT_FIELDS,
    defaultSort: defaultOnboardingLivesSortOf(period),
    parseFilters: parseOnboardingLivesFilters,
    writeFilters: writeOnboardingLivesFilters,
  });

  return (
    <Card>
      <div className={"mb-4"}>
        <SegmentedControl
          options={[
            { value: OnboardingLiveListPeriod.Upcoming, label: "Future" },
            { value: OnboardingLiveListPeriod.Past, label: "Già svolte" },
          ]}
          value={period}
          onChange={onPeriodChange}
        />
      </div>
      {loading && onboardingLives.length <= 0 ? (
        <LoadingRows />
      ) : (
        <OnboardingLivesTable
          lives={onboardingLives}
          period={period}
          sort={sort}
          onSortChange={setSort}
          filters={filters}
          onFiltersChange={setFilters}
          onOpen={(live) => openFromList(onboardingLiveDetailPathOf(live.id))}
        />
      )}
    </Card>
  );
}

function OnboardingLivePlansList() {
  const openFromList = useOpenFromOnboardingLivesList();
  const { intents, loading } = useOnboardingLiveCreationIntents();
  const { sort, filters, setSort, setFilters } = useTableUrlState({
    sortFields: ONBOARDING_LIVE_PLANS_SORT_FIELDS,
    defaultSort: ONBOARDING_LIVE_PLANS_DEFAULT_SORT,
    parseFilters: parseOnboardingLivePlansFilters,
    writeFilters: writeOnboardingLivePlansFilters,
  });

  return (
    <Card>
      {loading && intents.length <= 0 ? (
        <LoadingRows />
      ) : (
        <OnboardingLivePlansTable
          intents={intents}
          sort={sort}
          onSortChange={setSort}
          filters={filters}
          onFiltersChange={setFilters}
          onOpen={(intent) => openFromList(onboardingLivePlanPathOf(intent.id))}
        />
      )}
    </Card>
  );
}

export function OnboardingLivesScreen() {
  const { tab, period, setTab, setPeriod } = useOnboardingLivesScreenUrlState();
  const openFromList = useOpenFromOnboardingLivesList();
  return (
    <Layout>
      <Card>
        <div
          className={
            "flex flex-col md:flex-row md:items-center md:justify-between gap-3"
          }
        >
          <ScreenTitleBlock title={"Live onboarding"} />
          <div className={"flex items-center gap-2"}>
            <NewOnboardingLivePlanButton />
            <IconButton
              label={"Statistiche"}
              icon={ChartBarIcon}
              onClick={() => openFromList(OnboardingLiveStatisticsScreenPath)}
            />
            <SegmentedControl
              options={[
                { value: OnboardingLivesTab.Lives, label: "Live" },
                { value: OnboardingLivesTab.Plans, label: "Pianificazioni" },
              ]}
              value={tab}
              onChange={setTab}
              size={"md"}
            />
          </div>
        </div>
      </Card>
      <div className={"mt-4"}>
        {tab === OnboardingLivesTab.Lives ? (
          <OnboardingLivesList period={period} onPeriodChange={setPeriod} />
        ) : (
          <OnboardingLivePlansList />
        )}
      </div>
    </Layout>
  );
}
